import type { Row } from '@tanstack/react-table'
import type { DocumentPreviewPanelProps } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import {
  DocumentTableColumns,
  type DocumentTableRow,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentPreviewPanelState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentPreviewPanelState'
import { useDocumentResultsAnalytics } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsAnalytics'
import { useDocumentResultsFilterState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsFilterState'
import { useDocumentSearchNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchNavigation'
import { useDocumentSearchResultFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchResultFilters'
import type {
  DocumentSearchResult,
  Highlight,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { SearchActionsContextType } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useRef } from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export interface UseDocumentResultsViewStateParams {
  activeTabId: string
  isGeckoUser: boolean
  siteId: string
  searchState: SearchHistoryItem['searchState']
  contentFilter: DocumentsViewerContentFilter
  viewMode: ViewMode
  selectedDocumentId: string | null
  documentResults: DocumentTableRow[]
  elasticsearchHighlightsMap: Map<string, Highlight[]>
  rawSearchData: DocumentSearchResult | undefined
  searchLatencyMs: number | undefined
  isLinkedDocsMode: boolean
  showIndividualScores: boolean
  onSearchStateChange: (updates: SearchStateUpdate) => void
  onSelectDocument: SearchActionsContextType['onSelectDocument']
  onOpenNewTab: SearchActionsContextType['onOpenNewTab']
  onAddRecentDocument: SearchActionsContextType['onAddRecentDocument']
  onSnapshotSearchResults: SearchActionsContextType['onSnapshotSearchResults']
  setSelectedDocumentId: (id: string) => void
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void
}

export interface UseDocumentResultsViewStateResult {
  activeDocument: DocumentTableRow | null
  activeIndex: number
  cardFilters: CardFilterValues
  commitCardFiltersToUrl: () => void
  contentAreaRef: React.MutableRefObject<HTMLDivElement | null>
  detailScores: Record<string, number> | undefined
  documentPreviewPanelProps: Omit<DocumentPreviewPanelProps, 'onAddToContext'>
  filteredDocuments: DocumentTableRow[]
  handleCardFiltersChange: (next: CardFilterValues) => void
  handleOpenPreview: () => void
  handleRowFocus: (row: Row<DocumentTableRow>) => void
  hasActiveFilters: boolean
  isLoadingPreview: boolean
  isPreviewOpen: boolean
  onSearchDocumentNavigate: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean
  ) => void
  onViewModeChange: (value: string | number) => void
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  tableColumns: ReturnType<typeof DocumentTableColumns>
  tableFilteredCount: number
  tableHasActiveFilters: boolean
  handleTableFilteredDataChange: ReturnType<
    typeof useDocumentSearchResultFilters
  >['handleTableFilteredDataChange']
}

export function useDocumentResultsViewState({
  activeTabId,
  isGeckoUser,
  siteId,
  searchState,
  contentFilter,
  viewMode,
  selectedDocumentId,
  documentResults,
  elasticsearchHighlightsMap,
  rawSearchData,
  searchLatencyMs,
  isLinkedDocsMode,
  showIndividualScores,
  onSearchStateChange,
  onSelectDocument,
  onOpenNewTab,
  onAddRecentDocument,
  onSnapshotSearchResults,
  setSelectedDocumentId,
  handleViewModeChange,
}: UseDocumentResultsViewStateParams): UseDocumentResultsViewStateResult {
  const {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    filteredDocuments,
    handleCardFiltersChange,
    handleTableFilteredDataChange,
    hasActiveFilters,
    onViewModeChange,
    setActiveIndex,
    tableColumns,
    tableFilteredCount,
    tableHasActiveFilters,
  } = useDocumentResultsFilterState({
    documentResults,
    handleViewModeChange,
    onSearchStateChange,
    searchStateFilters: searchState.filters,
    searchQuery: searchState.query,
    selectedDocumentId,
    viewMode,
  })

  const contentAreaRef = useRef<HTMLDivElement>(null)

  const onSearchDocumentNavigate = useDocumentSearchNavigation({
    activeTabId,
    cardFilters,
    documentResults,
    isGeckoUser,
    onAddRecentDocument,
    onOpenNewTab,
    onSelectDocument,
    onSnapshotSearchResults,
    rawSearchData,
    searchQuery: searchState.query,
    siteId,
  })

  const {
    activeDocument,
    detailScores,
    documentPreviewPanelProps,
    handleOpenPreview,
    isLoadingPreview,
    isPreviewOpen,
  } = useDocumentPreviewPanelState({
    activeIndex,
    contentFilter,
    documentResults,
    elasticsearchHighlightsMap,
    filteredDocuments,
    isLinkedDocsMode,
    onOpenNewTab,
    onSearchDocumentNavigate,
    rawSearchData,
    searchQuery: searchState.query,
    selectedDocumentId,
    showIndividualScores,
    viewMode,
  })

  const handleActiveIndexChange: React.Dispatch<React.SetStateAction<number>> =
    useCallback(
      (nextActiveIndex) => {
        handleOpenPreview()
        setActiveIndex(nextActiveIndex)
      },
      [handleOpenPreview, setActiveIndex]
    )

  useDocumentResultsAnalytics({
    activeDocument,
    documentResults,
    rawSearchData,
    searchLatencyMs,
    searchQuery: searchState.query,
    siteId,
  })

  const handleRowFocus = useCallback(
    (row: Row<DocumentTableRow>) => {
      setSelectedDocumentId(row.original.id)
    },
    [setSelectedDocumentId]
  )

  useEffect(() => {
    if (viewMode !== 'table' || !selectedDocumentId || !contentAreaRef.current)
      return
    const id = selectedDocumentId
    const rafId = requestAnimationFrame(() => {
      const row = contentAreaRef.current?.querySelector(
        `[data-row-id="${CSS.escape(id)}"]`
      ) as HTMLElement | null
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return () => cancelAnimationFrame(rafId)
  }, [viewMode, selectedDocumentId])

  return {
    activeDocument,
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    contentAreaRef,
    detailScores,
    documentPreviewPanelProps,
    filteredDocuments,
    handleCardFiltersChange,
    handleOpenPreview,
    handleRowFocus,
    hasActiveFilters,
    isLoadingPreview,
    isPreviewOpen,
    onSearchDocumentNavigate,
    onViewModeChange,
    setActiveIndex: handleActiveIndexChange,
    tableColumns,
    tableFilteredCount,
    tableHasActiveFilters,
    handleTableFilteredDataChange,
  }
}
